import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import BgColorContainer from '../../Layout/BgColorContainer';
import ScrollWords from '../../ui/cinematic/ScrollWords';
import PillLink from '../../ui/cinematic/PillLink';
import useParallax from '../../../hooks/useParallax';
import useReveal from '../../../hooks/useReveal';
import useCursorZone from '../../../hooks/useCursorZone';
import { elemento, partes } from '../../../cms/textos';
// Fotos de equipo (stock que el sitio ya usa en "promesas"); los retratos
// individuales quedan para la página Nosotros
import equipoMesa from '../../../assets/home/equipo-mesa.webp';
import equipoCelebra from '../../../assets/home/equipo-celebra.webp';

/** Textos de "Páginas → Inicio" (Home.jsx). */
function Nosotros({ textos }) {
    const sectionRef = useRef(null);
    const textRef = useReveal();
    const fotoGrandeRef = useParallax({ y: -8 });
    const fotoChicaRef = useParallax({ y: -10 });
    const cursorZone = useCursorZone('dark');
    const Ceja = elemento(textos, 'ceja', 'p');
    const Parrafo1 = elemento(textos, 'parrafo1', 'p');
    const Parrafo2 = elemento(textos, 'parrafo2', 'p');

    // Las fotos entran "abriéndose" desde abajo (clip-path), una sola vez
    useGSAP(() => {
        gsap.utils.toArray('.nosotros-foto').forEach((el, i) => {
            gsap.fromTo(el,
                { clipPath: 'inset(100% 0% 0% 0%)' },
                {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.4,
                    delay: i * 0.15,
                    ease: 'expo.out',
                    scrollTrigger: { trigger: el, start: 'top 85%', once: true },
                });
        });
    }, { scope: sectionRef });

    return (
        <BgColorContainer>
            {/* Sube 50svh sobre el final del hero (que sigue fijo) con un fondo que
                parte transparente: el titular entra mientras el video se disuelve,
                en vez de dejar una pantalla negra entre las dos secciones. */}
            <section ref={sectionRef} id="nosotros" className="hero-overlap relative z-10 -mt-[50svh] overflow-hidden py-28 text-cream md:py-44" {...cursorZone}>
                <div className="container relative px-5 md:px-10 2xl:px-0">
                    <Ceja className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-cream/50 md:text-sm">
                        <span>01</span>
                        <span className="h-px w-10 bg-cream/30" />
                        <span>{textos.ceja}</span>
                    </Ceja>

                    {/* El texto va junto al titular, no al pie del collage: abajo de
                        620 px de fotos quedaba fuera de la primera vista */}
                    <div className="mt-10 grid grid-cols-1 items-end gap-10 lg:grid-cols-12">
                        <ScrollWords
                            as={elemento(textos, 'titular', 'h2')}
                            className="text-[2.6rem] font-medium leading-[1.02] tracking-tight md:text-7xl lg:col-span-7 xl:text-[6.5rem]"
                            parts={partes(textos.titular)}
                        />

                        <div ref={textRef} className="flex max-w-xl flex-col gap-5 md:ml-auto md:w-1/2 lg:col-span-4 lg:col-start-9 lg:ml-0 lg:w-auto lg:pb-3">
                            <Parrafo1 data-reveal className="text-lg leading-relaxed text-cream/70">
                                {textos.parrafo1}
                            </Parrafo1>
                            <Parrafo2 data-reveal className="text-lg leading-relaxed text-cream/70">
                                {textos.parrafo2}
                            </Parrafo2>
                            <div data-reveal className="pt-3">
                                <PillLink to="/aboutus">{textos.boton}</PillLink>
                            </div>
                        </div>
                    </div>

                    {/* Collage: dos fotos de equipo con parallax a distinta velocidad.
                        La foto se desplaza dentro de un marco más bajo que ella
                        (no se escala), así no pierde nitidez. En blanco y negro
                        para la paleta negro/crema; a color al pasar el mouse.
                        Solo transiciona el filtro: el transform lo maneja GSAP.
                        En mobile se montan; desde md van escalonadas a lo ancho. */}
                    <div className="relative mt-16 h-[460px] sm:h-[560px] md:mt-24 md:aspect-[100/46] md:h-auto">
                        <div className="nosotros-foto group absolute left-0 top-0 aspect-[16/10] w-[82%] overflow-hidden md:w-[64%]">
                            <img
                                ref={fotoGrandeRef}
                                src={equipoMesa}
                                alt="Equipo trabajando en conjunto alrededor de una mesa con notebooks y tablets"
                                loading="lazy"
                                className="absolute inset-x-0 top-0 h-[112%] w-full object-cover grayscale transition-[filter] duration-700 group-hover:grayscale-0"
                            />
                        </div>
                        <div className="nosotros-foto group absolute bottom-0 right-0 aspect-[4/5] w-[46%] overflow-hidden ring-[10px] ring-ink md:w-[30%] md:ring-0">
                            <img
                                ref={fotoChicaRef}
                                src={equipoCelebra}
                                alt="Equipo celebrando con un choque de manos en la oficina"
                                loading="lazy"
                                className="absolute inset-x-0 top-0 h-[115%] w-full object-cover grayscale transition-[filter] duration-700 group-hover:grayscale-0"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </BgColorContainer>
    );
}

export default Nosotros;
